import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Loader2,
  MapPin,
  School,
  ShoppingCart,
  Hospital,
  Bus,
  Car,
  Utensils,
  Heart,
  Shield,
  DollarSign,
  MessageCircle,
  Lightbulb,
  Home,
  Building,
  Star,
  Users,
  Coffee,
  TreePine
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NeighborhoodGuidePage() {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationName, setLocationName] = useState('');

  useEffect(() => {
    fetchGuide();
  }, [locationId]);

  const fetchGuide = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First get location name if we have an ID
      if (locationId) {
        const locResponse = await fetch(`${API_URL}/api/locations/${locationId}`);
        if (locResponse.ok) {
          const locData = await locResponse.json();
          setLocationName(locData.name || 'Unknown');
        }
      }

      const response = await fetch(`${API_URL}/api/ai/neighborhood-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location_id: locationId })
      });

      if (response.ok) {
        const data = await response.json();
        setGuide(data);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to load guide');
      }
    } catch (err) {
      console.error('Error fetching guide:', err);
      setError('Failed to load neighborhood guide');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-signal-blue mb-4" />
        <p className="text-slate-600">{t('neighborhood.generating', 'Generating neighborhood guide...')}</p>
        <p className="text-sm text-slate-800 mt-2">{t('neighborhood.loading_hint', 'This may take a moment')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
            <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Guide Unavailable</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate(-1)} variant="outline" className="border-slate-300">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="neighborhood-guide-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-200 mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-signal-blue/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <MapPin className="w-7 h-7 text-signal-blue" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-800">
                {guide?.area_name || locationName || 'Neighborhood Guide'}
              </h1>
              <p className="text-slate-600 mt-1">{guide?.overview}</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {guide && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 text-center">
              <Shield className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
              <p className="text-xs text-slate-800">Safety</p>
              <p className="text-sm font-medium text-slate-200">{guide.safety_rating || 'Safe'}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 text-center">
              <DollarSign className="w-5 h-5 text-military-gold mx-auto mb-2" />
              <p className="text-xs text-slate-800">{t('neighborhood.cost_of_living', 'Cost of Living')}</p>
              <p className="text-sm font-medium text-slate-200">{guide.cost_of_living || 'Moderate'}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 text-center">
              <MessageCircle className="w-5 h-5 text-signal-blue mx-auto mb-2" />
              <p className="text-xs text-slate-800">English Friendly</p>
              <p className="text-sm font-medium text-slate-200">{guide.english_friendly_score || '7'}/10</p>
            </div>
          </div>
        )}

        {/* Military Info */}
        {guide?.military_info && (
          <Section title="Military Resources" icon={<Building className="w-5 h-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard label="Nearest Base" value={guide.military_info.nearest_base} />
              <InfoCard label="Commissary" value={guide.military_info.commissary} />
              <InfoCard label="PX/BX" value={guide.military_info.px_bx} />
              <InfoCard label="ID Card Office" value={guide.military_info.id_card_office} />
            </div>
          </Section>
        )}

        {/* Schools */}
        {guide?.schools && (
          <Section title="Schools" icon={<School className="w-5 h-5" />}>
            <div className="space-y-4">
              {guide.schools.dodea?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-military-gold mb-2">DoDEA Schools</h4>
                  <div className="flex flex-wrap gap-2">
                    {guide.schools.dodea.map((school, i) => (
                      <Badge key={i} className="bg-military-gold/20 text-military-gold border-military-gold/30">
                        {school}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {guide.schools.international?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-signal-blue mb-2">International Schools</h4>
                  <div className="flex flex-wrap gap-2">
                    {guide.schools.international.map((school, i) => (
                      <Badge key={i} className="bg-signal-blue/20 text-signal-blue border-signal-blue/30">
                        {school}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {guide.schools.german_schools?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2">German Schools (English Support)</h4>
                  <div className="flex flex-wrap gap-2">
                    {guide.schools.german_schools.map((school, i) => (
                      <Badge key={i} variant="outline" className="border-slate-600 text-slate-300">
                        {school}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Daily Life */}
        {guide?.daily_life && (
          <Section title="Daily Life" icon={<ShoppingCart className="w-5 h-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard 
                label="Grocery Stores" 
                value={Array.isArray(guide.daily_life.grocery_stores) 
                  ? guide.daily_life.grocery_stores.join(', ') 
                  : guide.daily_life.grocery_stores} 
                icon={<ShoppingCart className="w-4 h-4" />}
              />
              <InfoCard 
                label="Healthcare" 
                value={guide.daily_life.healthcare} 
                icon={<Hospital className="w-4 h-4" />}
              />
              <InfoCard 
                label="Public Transport" 
                value={guide.daily_life.public_transport} 
                icon={<Bus className="w-4 h-4" />}
              />
              <InfoCard 
                label="Parking" 
                value={guide.daily_life.parking} 
                icon={<Car className="w-4 h-4" />}
              />
            </div>
          </Section>
        )}

        {/* Housing Tips */}
        {guide?.housing_tips?.length > 0 && (
          <Section title="Housing Tips" icon={<Home className="w-5 h-5" />}>
            <ul className="space-y-3">
              {guide.housing_tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-signal-blue/20 text-signal-blue rounded-full flex items-center justify-center text-xs flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-slate-300">{tip}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Family Activities */}
        {guide?.family_activities?.length > 0 && (
          <Section title="Family Activities" icon={<Users className="w-5 h-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {guide.family_activities.map((activity, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                  <TreePine className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{activity}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Dining */}
        {guide?.dining && (
          <Section title="Dining" icon={<Utensils className="w-5 h-5" />}>
            <div className="space-y-4">
              {guide.dining.american_friendly?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                    <Coffee className="w-4 h-4" /> American-Friendly Restaurants
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {guide.dining.american_friendly.map((place, i) => (
                      <Badge key={i} variant="outline" className="border-slate-600 text-slate-300">
                        {place}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {guide.dining.local_favorites?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                    <Star className="w-4 h-4 text-military-gold" /> Local Favorites
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {guide.dining.local_favorites.map((place, i) => (
                      <Badge key={i} className="bg-military-gold/20 text-military-gold border-military-gold/30">
                        {place}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Insider Tips */}
        {guide?.insider_tips?.length > 0 && (
          <Section title="Insider Tips" icon={<Lightbulb className="w-5 h-5" />} highlight>
            <ul className="space-y-3">
              {guide.insider_tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Lightbulb className="w-4 h-4 text-military-gold flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{tip}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* CTA */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
          <h3 className="font-bold text-slate-800 mb-2">{t('neighborhood.cta_title', 'Ready to find your home?')}</h3>
          <p className="text-slate-600 text-sm mb-4">{t('neighborhood.cta_subtitle', 'Browse available properties in this area')}</p>
          <Link to={`/listings?location=${locationId || ''}`}>
            <Button className="bg-signal-blue hover:bg-signal-blue-hover text-white">
              {t('neighborhood.view_properties', 'View Properties')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

// Helper Components
function Section({ title, icon, children, highlight = false }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6 ${highlight ? 'border-military-gold/30 bg-military-gold/5' : ''}`}>
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-800 mb-4">
        <span className={highlight ? 'text-military-gold' : 'text-signal-blue'}>{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function InfoCard({ label, value, icon }) {
  if (!value) return null;
  return (
    <div className="p-3 bg-white/50 rounded-lg">
      <div className="flex items-center gap-2 text-slate-800 text-xs mb-1">
        {icon}
        {label}
      </div>
      <p className="text-slate-200 text-sm">{value}</p>
    </div>
  );
}
